/**
 * Tab "Richieste Materiali" dentro CantiereDetail (usato anche per Manutenzioni).
 * Filtra le richieste per cantiere_id e permette di crearne una nuova con il
 * cantiere pre-selezionato e bloccato.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api";
import ScansioneInventario from "@/pages/magazzino/ScansioneInventario";
import {
  Plus, FileDown, Mail, MessageCircle, Trash2, Package, Send,
  CheckCircle2, AlertTriangle, RefreshCw,
} from "lucide-react";
import { NuovaRichiestaModal, STATO_MAP, EMPTY_RIGA } from "@/pages/RichiesteMateriali";
import { AccettaRichiestaModal, RifiutaRichiestaButton } from "@/pages/richieste/AccettaRifiuta";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RichiesteMaterialiTab({ cantiere }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [accettaFor, setAccettaFor] = useState(null);
  const [scannerFor, setScannerFor] = useState(null);

  const { data: richieste = [], isLoading } = useQuery({
    queryKey: ["richieste-materiali", cantiere.cantiere_id],
    queryFn: () =>
      api.get(`/richieste-materiali?cantiere_id=${encodeURIComponent(cantiere.cantiere_id)}`)
         .then((r) => r.data),
  });

  const { data: fornitori = [] } = useQuery({
    queryKey: ["fornitori-anagrafica"],
    queryFn: () => api.get("/entita/fornitori").then((r) => r.data),
  });

  const create = useMutation({
    mutationFn: (payload) => api.post("/richieste-materiali", payload).then((r) => r.data),
    onSuccess: async (data) => {
      qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
      if (data.email_inviata) {
        toast.success(`${data.numero_richiesta} — email inviata al fornitore`);
      } else if (data.fornitore_email) {
        toast.warning(`${data.numero_richiesta} creata, ma email non inviata: ${data.email_error || "SMTP non configurato"}`);
      } else {
        toast.success(`${data.numero_richiesta} creata (nessuna email — fornitore senza indirizzo)`);
      }
      setOpen(false);
      // Auto-invio WhatsApp se il fornitore ha un numero di telefono
      if (data.fornitore_telefono) {
        toast.info("Apro WhatsApp per il fornitore…", { duration: 2500 });
        await new Promise((r) => setTimeout(r, 600));
        whatsappShare(data);
      }
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore creazione richiesta"),
  });

  const cambiaStato = useMutation({
    mutationFn: ({ id, stato }) =>
      api.patch(`/richieste-materiali/${id}/stato`, { stato }).then((r) => r.data),
    onSuccess: () => {
      toast.success("Stato aggiornato");
      qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
    },
  });

  const reinviaEmail = useMutation({
    mutationFn: (id) => api.post(`/richieste-materiali/${id}/reinvia-email`).then((r) => r.data),
    onSuccess: (data) => {
      if (data.ok) toast.success("Email reinviata al fornitore");
      else toast.error(`Invio fallito: ${data.result?.error || "SMTP non configurato"}`);
      qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
    },
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/richieste-materiali/${id}`),
    onSuccess: () => {
      toast.success("Richiesta eliminata");
      qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
    },
  });

  const whatsappShare = async (r) => {
    const popup = window.open("", "_blank");

    try {
      const res = await api.post(`/richieste-materiali/${r.richiesta_id}/share`);

      const relativePath = res.data.path || res.data.share_url;
      const publicUrl =
        res.data.share_url && res.data.share_url.startsWith("http")
          ? res.data.share_url
          : `${REACT_APP_BACKEND_URL}${relativePath}`;

      const righeSummary = (r.materiali || [])
        .slice(0, 5)
        .map((m) => {
          const codice = m.codice_articolo ? `Cod. ${m.codice_articolo} — ` : "";
          return `• ${codice}${m.descrizione} — ${m.quantita} ${m.unita_misura}`;
        })
        .join("\n");

      const extra =
        (r.materiali || []).length > 5
          ? `\n…e altre ${(r.materiali || []).length - 5}`
          : "";

      const message = [
        `Buongiorno, richiesta materiali *${r.numero_richiesta}*`,
        `Cantiere: ${r.cantiere_nome}`,
        r.data_consegna_richiesta
          ? `Consegna entro: ${r.data_consegna_richiesta}`
          : "",
        "",
        righeSummary + extra,
        "",
        `PDF ordine: ${publicUrl}`,
        "",
        "Alla consegna riportare sulla bolla questo numero. Grazie!",
      ]
        .filter(Boolean)
        .join("\n");

      const phone = (r.fornitore_telefono || "").replace(/[^\d+]/g, "");
      const cleanPhone = phone.replace(/^\+/, "");

      const url = cleanPhone
        ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`;

      if (popup) {
        popup.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (e) {
      if (popup) popup.close();
      toast.error(e.response?.data?.detail || "Errore generazione link WhatsApp");
    }
  };

  const totRighe = richieste.reduce((s, r) => s + (r.materiali?.length || 0), 0);

  return (
    <div className="bg-white border border-slate-200" data-testid="cantiere-richieste-materiali-tab">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">Ordini a fornitore</div>
          <h3 className="font-display font-bold flex items-center gap-2">
            <Package className="w-4 h-4 text-slate-500" />
            {richieste.length} richieste · {totRighe} righe totali
          </h3>
          <p className="text-[11px] text-slate-500 mt-1">
            Ordini materiali inviati al fornitore per questo {cantiere.tipo === "manutenzione" ? "manutenzione" : "cantiere"}.
            Alla creazione parte l'email con PDF allegato; quando arriva la bolla la richiesta diventa <b>ritirata</b>.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="btn-nuova-richiesta-cantiere"
          className="inline-flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-sm shadow-brutal"
        >
          <Plus className="w-4 h-4" /> Nuova richiesta materiali
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">N°</th>
              <th className="text-left p-3">Data</th>
              <th className="text-left p-3">Fornitore</th>
              <th className="text-right p-3">Righe</th>
              <th className="text-left p-3">Stato</th>
              <th className="text-left p-3">Email</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-400 italic">Caricamento…</td></tr>
            )}
            {!isLoading && richieste.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-slate-400 text-sm">
                  Nessuna richiesta materiali per questo {cantiere.tipo === "manutenzione" ? "manutenzione" : "cantiere"}.<br />
                  <span className="text-[11px]">Clicca <b>Nuova richiesta materiali</b> per creare il primo ordine al fornitore.</span>
                </td>
              </tr>
            )}
            {richieste.map((r) => {
              const S = STATO_MAP[r.stato] || STATO_MAP.inviata;
              const emailKo = r.stato === "inviata" && !r.email_inviata && r.fornitore_email;
              return (
                <tr key={r.richiesta_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`row-rm-cantiere-${r.numero_richiesta}`}>
                  <td className="p-3 font-mono font-bold text-orange-700">{r.numero_richiesta}</td>
                  <td className="p-3 text-xs">{r.data_richiesta}</td>
                  <td className="p-3">
                    <div>{r.fornitore_nome}</div>
                    {r.fornitore_email && <div className="text-[11px] text-slate-500 font-mono">{r.fornitore_email}</div>}
                  </td>
                  <td className="p-3 text-right font-mono">{(r.materiali || []).length}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-sm text-[11px] font-semibold ${S.cls}`}>
                      <S.icon className="w-3 h-3" /> {S.label}
                    </span>
                  </td>
                  <td className="p-3 text-xs">
                    {r.email_inviata && (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> Inviata</span>
                    )}
                    {emailKo && (
                      <span className="inline-flex items-center gap-1 text-amber-700" title={r.email_error}>
                        <AlertTriangle className="w-3 h-3" /> Fallita
                      </span>
                    )}
                    {!r.fornitore_email && <span className="text-slate-400">senza email</span>}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      {(r.stato === "inviata" || r.stato === "in_attesa" || r.stato === "bozza") && (
                        <>
                          <button
                            onClick={() => setAccettaFor(r)}
                            data-testid={`btn-accetta-${r.numero_richiesta}`}
                            title="Accetta richiesta e genera rate"
                            className="p-1 text-emerald-700 hover:bg-emerald-50 border border-emerald-300 rounded-sm inline-flex items-center gap-1"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span className="text-[11px] font-semibold">Accetta</span>
                          </button>
                          <RifiutaRichiestaButton richiesta={r} onDone={() => qc.invalidateQueries({ queryKey: ["richieste-materiali"] })} />
                        </>
                      )}
                      {r.stato === "accettata" && (r.rate_pagamento || []).length > 0 && (
                        <span
                          title={`Rate: ${(r.rate_pagamento || []).map(p => `${p.numero}/${p.totale_rate}: €${p.importo.toFixed(2)} → ${p.data_scadenza}`).join('\n')}`}
                          className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5 rounded-sm"
                        >
                          {(r.rate_pagamento || []).length} rate
                        </span>
                      )}
                      {!["bozza", "annullata", "rifiutata", "ritirata"].includes(r.stato) && (
                        <button
                          type="button"
                          onClick={() => setScannerFor(r)}
                          title={`Preleva materiali per ${r.numero_richiesta}`}
                          className="px-2 py-1 text-amber-800 hover:bg-amber-50 border border-amber-300 rounded-sm inline-flex items-center gap-1"
                        >
                          <Package className="w-3.5 h-3.5" />
                          <span className="text-[11px] font-semibold">
                            Apri / Preleva
                          </span>
                        </button>
                      )}

                      <button
                        onClick={async () => {
                          try {
                            const res = await api.get(
                              `/richieste-materiali/${r.richiesta_id}/pdf`,
                              { responseType: "arraybuffer" }
                            );
                            const blob = new Blob([res.data], { type: "application/pdf" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `Richiesta_${r.numero_richiesta || "materiali"}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            setTimeout(() => URL.revokeObjectURL(url), 5000);
                          } catch (e) {
                            toast.error("Errore download PDF");
                          }
                        }}
                        title="Scarica PDF"
                        className="p-1 text-slate-700 hover:bg-slate-100 rounded-sm"
                      >
                        <FileDown className="w-4 h-4" />
                      </button>
                      {r.fornitore_email && r.stato !== "ritirata" && r.stato !== "annullata" && (
                        <button
                          onClick={() => reinviaEmail.mutate(r.richiesta_id)}
                          title="Reinvia email"
                          disabled={reinviaEmail.isPending}
                          className="p-1 text-blue-700 hover:bg-blue-50 rounded-sm"
                        >
                          {reinviaEmail.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        </button>
                      )}
                      {r.stato !== "annullata" && (
                        <button
                          onClick={() => whatsappShare(r)}
                          title={r.fornitore_telefono ? `WhatsApp a ${r.fornitore_telefono}` : "WhatsApp Web"}
                          className="p-1 text-emerald-700 hover:bg-emerald-50 rounded-sm"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </button>
                      )}
                      <select
                        value={r.stato}
                        onChange={(e) => cambiaStato.mutate({ id: r.richiesta_id, stato: e.target.value })}
                        className="text-[11px] px-1.5 py-1 border border-slate-300 rounded-sm bg-white"
                        title="Cambia stato"
                      >
                        {Object.entries(STATO_MAP).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => {
                          if (confirm(`Eliminare la richiesta ${r.numero_richiesta}?`)) del.mutate(r.richiesta_id);
                        }}
                        title="Elimina"
                        className="p-1 text-rose-700 hover:bg-rose-100 rounded-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {scannerFor && (
        <ScansioneInventario
          onClose={() => {
            setScannerFor(null);
            qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
            qc.invalidateQueries({ queryKey: ["magazzino"] });
            qc.invalidateQueries({ queryKey: ["magazzino-movimenti"] });
            qc.invalidateQueries({ queryKey: ["bolle"] });
          }}
          cantieri={[cantiere]}
          richiestaIniziale={scannerFor}
        />
      )}

      {open && (
        <NuovaRichiestaModal
          onClose={() => setOpen(false)}
          onSave={(payload) => create.mutate(payload)}
          saving={create.isPending}
          cantieri={[cantiere]}
          fornitori={fornitori}
          defaultCantiereId={cantiere.cantiere_id}
          lockCantiere={true}
        />
      )}
      {accettaFor && (
        <AccettaRichiestaModal
          richiesta={accettaFor}
          onClose={() => setAccettaFor(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ["richieste-materiali"] })}
        />
      )}
    </div>
  );
}
